import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';

import {Button} from 'UI/components/action/Button';

import {useUserNameEditor} from 'UI/hooks/useUserNameEditor';

/**
 * Inline bearbeitbares Namensfeld für die Account-Karte der Settings-Seite. Zeigt den Namen mit
 * einem "Bearbeiten"-Link an; ein Klick wechselt zu einem Formular mit Speichern/Abbrechen.
 *
 * @returns ReactNode des Namensfelds.
 */
const UserNameEditor = () => {
    const {
        cancelEditing,
        displayName,
        handleNameChange,
        handleSubmit,
        isEditing,
        name,
        startEditing,
        status,
        t
    } = useUserNameEditor();

    if (!isEditing) {
        return (
            <NameRow data-cy="UserNameEditor">
                <UserName data-cy="UserNameEditor-name">{displayName}</UserName>
                <EditButton data-cy="UserNameEditor-edit" type="button" onClick={startEditing}>
                    {t('account.editNameButton')}
                </EditButton>
            </NameRow>
        );
    }

    return (
        <Form data-cy="UserNameEditor-form" onSubmit={handleSubmit}>
            <NameInput
                data-cy="UserNameEditor-input"
                placeholder={t('account.namePlaceholder')}
                type="text"
                value={name}
                autoFocus
                required
                onChange={handleNameChange}
            />
            {status === 'required' && (
                <ErrorMessage data-cy="UserNameEditor-required">{t('account.nameRequired')}</ErrorMessage>
            )}
            {status === 'error' && (
                <ErrorMessage data-cy="UserNameEditor-error">{t('account.nameUpdateError')}</ErrorMessage>
            )}
            <ButtonRow>
                <Button
                    data-cy="UserNameEditor-save"
                    disabled={status === 'submitting'}
                    size="small"
                    type="submit"
                    variant="primaryGradient"
                >
                    {status === 'submitting' ? t('account.savingName') : t('account.saveNameButton')}
                </Button>
                <CancelButton
                    data-cy="UserNameEditor-cancel"
                    disabled={status === 'submitting'}
                    type="button"
                    onClick={cancelEditing}
                >
                    {t('account.cancelEditName')}
                </CancelButton>
            </ButtonRow>
        </Form>
    );
};

UserNameEditor.displayName = 'UserNameEditor';

export {UserNameEditor};

// ── Styled ──────────────────────────────────────────────────

const NameRow = styled.div`
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing(3)};
    max-width: 100%;
`;

const UserName = styled.span`
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 2rem;
    font-weight: 700;
    max-width: 100%;
    overflow-wrap: break-word;
`;

const EditButton = styled.button`
    background: none;
    border: none;
    color: ${({theme}) => theme.colors.accentDefault};
    cursor: pointer;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.3rem;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: ${spacing(3)};
    width: 100%;
`;

const NameInput = styled.input`
    border: 1px solid ${({theme}) => theme.colors.inkDisabled};
    border-radius: 8px;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.6rem;
    font-weight: 400;
    padding: ${spacing(3)} ${spacing(4)};
`;

const ErrorMessage = styled.p`
    color: ${({theme}) => theme.colors.accentDefault};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.3rem;
`;

const ButtonRow = styled.div`
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing(3)};
`;

const CancelButton = styled.button`
    background: none;
    border: none;
    color: ${({theme}) => theme.colors.inkTertiary};
    cursor: pointer;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.4rem;

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
`;